import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HealingIcon from '@mui/icons-material/Healing';
import axios from 'axios';
import { APIURL } from 'src/config';
import './AiCompanion.css';

const MAX_MESSAGE_LENGTH = 4000;
const STEP_DELAY_MS = 800;
const MAX_TEXTAREA_ROWS = 4;
const MAX_HISTORY_ENTRIES = 10;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildHistory(messages) {
  return messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .slice(-MAX_HISTORY_ENTRIES)
    .map((m) => ({ role: m.role, content: m.text }));
}

const PROMPT_CARDS = [
  {
    icon: DescriptionIcon,
    title: 'Summarize Patient Cases',
    subtitle: 'Consolidated summary across all your active patients',
    prompt: 'Summarize all patient cases for my active patients and provide a consolidated summary.',
  },
  {
    icon: AssessmentIcon,
    title: 'Review Encounters',
    subtitle: 'Analyze patient visits & encounters from last week and this week',
    prompt: 'Review recent encounters from last week and this week across all my customers and review for any issues.',
  },
  {
    icon: HealingIcon,
    title: 'Clinical Highlights',
    subtitle: 'Review assessments and clinical notes from last week and this week',
    prompt: 'Provide clinical highlights from assessments and notes from last week and this week across all my customers and review for any issues.',
  },
];

const markdownComponents = {
  h1: ({ children }) => <h1 className="md-h1">{children}</h1>,
  h2: ({ children }) => <h2 className="md-h2">{children}</h2>,
  h3: ({ children }) => <h3 className="md-h3">{children}</h3>,
  h4: ({ children }) => <h4 className="md-h4">{children}</h4>,
  p: ({ children }) => <p className="md-p">{children}</p>,
  strong: ({ children }) => <strong className="md-strong">{children}</strong>,
  em: ({ children }) => <em className="md-em">{children}</em>,
  ul: ({ children }) => <ul className="md-ul">{children}</ul>,
  ol: ({ children }) => <ol className="md-ol">{children}</ol>,
  li: ({ children }) => <li className="md-li">{children}</li>,
  blockquote: ({ children }) => <blockquote className="md-blockquote">{children}</blockquote>,
  code: ({ children }) => <code className="md-code-inline">{children}</code>,
  pre: ({ children }) => <pre className="md-code-block">{children}</pre>,
  table: ({ children }) => (
    <div className="md-table-wrapper">
      <table className="md-table">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => <th className="md-th">{children}</th>,
  td: ({ children }) => <td className="md-td">{children}</td>,
  hr: () => <hr className="md-hr" />,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="md-link">
      {children}
    </a>
  ),
};

export default function AiCompanion({ user }) {
  const [messages, setMessages] = useState([]);
  // Patient ids the API resolved on earlier turns. buildHistory only carries
  // message text, so without this the agent starts each turn with no id for a
  // patient it is visibly mid-conversation about -- and invents one.
  const [resolvedCustomers, setResolvedCustomers] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages.length, isProcessing, currentStep]);

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = 22;
    const maxHeight = lineHeight * MAX_TEXTAREA_ROWS;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [inputValue, resizeTextarea]);

  const newChat = useCallback(() => {
    setMessages([]);
    setResolvedCustomers([]);
    setInputValue('');
    setCurrentStep(null);
    setIsProcessing(false);
  }, []);

  const submitMessage = useCallback(async (textToSend) => {
    const text = (textToSend || inputValue).trim();
    if (!text || isProcessing) return;

    const history = buildHistory(messages);
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setInputValue('');

    // empId tells the API which of this email's employee records -- and so
    // which company's caseload -- the question is about. One email commonly
    // has a record in several companies, so sending the question without it
    // is not a smaller request, it is an unscoped one. Stop here rather than
    // let the API answer about whichever company it picks.
    if (!user?.emp_id) {
      setMessages((prev) => [
        ...prev,
        { role: 'error', text: 'Your session is missing its company selection. Please reload the page and sign in again before using the AI Companion.' },
      ]);
      return;
    }

    setIsProcessing(true);

    try {
      const response = await axios.post(`${APIURL}/case-summary-chat`, { userMessage: text, history, empId: user.emp_id, resolvedCustomers });
      const { steps = [], final_answer, resolvedCustomers: nextResolved } = response.data;

      if (Array.isArray(nextResolved)) setResolvedCustomers(nextResolved);

      for (const step of steps) {
        setCurrentStep(step);
        await sleep(STEP_DELAY_MS);
      }

      setCurrentStep(null);
      setMessages((prev) => [...prev, { role: 'assistant', text: final_answer || '' }]);
    } catch (e) {
      setCurrentStep(null);
      setMessages((prev) => [
        ...prev,
        { role: 'error', text: e.response?.data?.message || 'Something went wrong. Please try again.' },
      ]);
    } finally {
      setIsProcessing(false);
    }
  }, [inputValue, isProcessing, messages, resolvedCustomers, user?.emp_id]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitMessage();
    }
  }, [submitMessage]);

  const handleCardClick = useCallback((prompt) => {
    submitMessage(prompt);
  }, [submitMessage]);

  const handleCopy = useCallback((text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex((prev) => (prev === index ? null : prev)), 1500);
  }, []);

  const isEmpty = messages.length === 0;

  return (
    <div className="ai-companion-container">
      <div className="ai-companion-header">
        <div className="ai-companion-title">
          <AutoAwesomeIcon style={{ color: '#3FA7D6' }} />
          AI Companion
        </div>
        <Tooltip title="New Chat">
          <IconButton size="small" onClick={newChat}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {isEmpty ? (
        <div className="ai-companion-body-empty">
          <div className="ai-companion-hero">
            <h2>Hi {user?.emp_name || 'there'} 👋</h2>
            <h3>What do you want to know?</h3>
            <p>I can help with patient case summaries, clinical document analysis, and health queries.</p>
          </div>

          <div className="ai-companion-cards-grid">
            {PROMPT_CARDS.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="ai-companion-card"
                  onClick={() => handleCardClick(card.prompt)}
                >
                  <Icon style={{ color: '#3FA7D6', marginBottom: 6 }} fontSize="small" />
                  <div className="ai-companion-card-title">{card.title}</div>
                  <div className="ai-companion-card-sub">{card.subtitle}</div>
                </div>
              );
            })}
          </div>

          <div className="ai-companion-input-wrapper ai-companion-input-wrapper-center">
            <div className="ai-companion-input-card">
              <textarea
                ref={textareaRef}
                className="ai-companion-textarea"
                rows={1}
                placeholder="Ask AI Companion..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isProcessing}
                maxLength={MAX_MESSAGE_LENGTH}
              />
              <div className="ai-companion-input-actions">
                <div className="ai-companion-input-actions-right">
                  <IconButton
                    size="small"
                    onClick={() => submitMessage()}
                    disabled={isProcessing || !inputValue.trim()}
                    style={{ color: isProcessing || !inputValue.trim() ? '#bdbdbd' : '#3FA7D6' }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            </div>
            <div className="ai-companion-disclaimer">
              AI Companion assists with patient care and case documentation. Always verify clinical information.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="ai-companion-messages" ref={messagesRef}>
            {messages.map((m, i) => (
              <div key={i} className={`ai-companion-message-row ai-companion-message-row-${m.role}`}>
                <div className={`ai-companion-bubble ai-companion-bubble-${m.role}`}>
                  {m.role === 'assistant' ? (
                    <ReactMarkdown components={markdownComponents}>{m.text}</ReactMarkdown>
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{m.text}</span>
                  )}
                  {m.role === 'assistant' && (
                    <Tooltip title={copiedIndex === i ? 'Copied!' : 'Copy'}>
                      <IconButton size="small" className="ai-companion-copy-btn" onClick={() => handleCopy(m.text, i)}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
            {isProcessing && (
              <div className="ai-companion-loading-row">
                <CircularProgress size={16} />
                <span>{currentStep || 'Thinking...'}</span>
              </div>
            )}
          </div>

          <div className="ai-companion-input-wrapper ai-companion-input-wrapper-bottom">
            <div className="ai-companion-input-card">
              <textarea
                ref={textareaRef}
                className="ai-companion-textarea"
                rows={1}
                placeholder="Ask AI Companion..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isProcessing}
                maxLength={MAX_MESSAGE_LENGTH}
              />
              <div className="ai-companion-input-actions">
                <div className="ai-companion-input-actions-right">
                  <IconButton
                    size="small"
                    onClick={() => submitMessage()}
                    disabled={isProcessing || !inputValue.trim()}
                    style={{ color: isProcessing || !inputValue.trim() ? '#bdbdbd' : '#3FA7D6' }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            </div>
            <div className="ai-companion-disclaimer">
              AI Companion assists with patient care and case documentation. Always verify clinical information.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
